import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/**
 * El código de una pieza, tratado como lo que es: el idioma del negocio.
 *
 * `ACL001`, `ARB102-1`, `VNA302-1`. Es lo que el cliente dice por teléfono, lo que busca y lo
 * único que identifica una pieza sin ambigüedad. Hasta ahora se dibujaba como texto en negrita
 * común, igual que cualquier otra cosa.
 *
 * Tres decisiones, y ninguna es decorativa:
 *
 * - `tabular-nums`: los dígitos ocupan todos el mismo ancho, así que en una columna de códigos
 *   los números caen alineados y `ACS001-4` y `ACS001-11` se comparan de un vistazo. Con las
 *   cifras proporcionales que trae la fuente por defecto, cada renglón arranca corrido.
 * - La condensada: es la familia de los títulos del sitio, y un código es un título, no prosa.
 * - El tracking abierto: al revés que los títulos. Una secuencia sin palabras se lee carácter
 *   por carácter, y juntarlos hace que `ARB102-1` parezca una sola mancha.
 */
export function CodigoDePieza({
  codigo,
  variante = 'normal',
  component = 'span',
  sx,
}: {
  codigo: string;
  /** `display` es para la ficha del producto, donde el código es el título de la página. */
  variante?: 'normal' | 'display' | 'menor';
  component?: React.ElementType;
  sx?: SxProps<Theme>;
}) {
  const tamanios = {
    display: { fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', letterSpacing: '0.01em', lineHeight: 1 },
    normal: { fontSize: '1.05rem', letterSpacing: '0.06em', lineHeight: 1.2 },
    menor: { fontSize: '0.8125rem', letterSpacing: '0.08em', lineHeight: 1.2 },
  } as const;

  return (
    <Box
      component={component}
      sx={[
        {
          fontFamily: 'var(--font-roboto-condensed)',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum" 1',
          display: 'inline-block',
          ...tamanios[variante],
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {codigo}
    </Box>
  );
}
